import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Box,
  IconButton,
  MenuItem,
  Select,
  FormControl,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { useNavigate, useOutletContext } from 'react-router-dom';
import PropertyFooterLayout from '../../../components/properties/PropertyFooterLayout';
import { SelectChangeEvent } from '@mui/material';
import { useAuth } from '../../../context/AuthContext';

const progress = 30;
const step = 3;

const MIN_PRICE_CRC = 5000;
const MAX_PRICE_CRC = 50000000000;
const MIN_PRICE_USD = 10;
const MAX_PRICE_USD = 1000000000;

type PublishOutletContext = {
  handleNavigateToPropertyResume: () => void;
  handleNavigateToRealtorSelection: () => void
  propertyPrice: string;
  setPropertyPrice: (propertyPrice: string) => void;
  propertyCurrency: string;
  setPropertyCurrency: (propertyCurrency: string) => void;
};

const PropertyPricePage: React.FC = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false)
  const navigate = useNavigate();
  const { userToken} = useAuth();
  const {
    handleNavigateToPropertyResume,
    handleNavigateToRealtorSelection,
    propertyPrice,
    setPropertyPrice,
    propertyCurrency,
    setPropertyCurrency,
  } = useOutletContext<PublishOutletContext>();

  useEffect(() => {
    if (userToken) {
      const adminProfile = userToken.user.profiles.find((profile) => profile.roleId === 8);
      setIsAdmin(!!adminProfile); // Convertir a booleano, será true solo si existe adminProfile
      console.log("¿Es administrador?", !!adminProfile, "Perfiles:", userToken.user.profiles.map(p => p.roleId));
    }
  }, [userToken]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleContinue = () => {
    setPropertyPrice(propertyPrice);
    setPropertyCurrency(propertyCurrency);
    if (isAdmin) {
      handleNavigateToRealtorSelection()
    } else {
      handleNavigateToPropertyResume()
    }
  };

  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;

    if (propertyCurrency === 'USD') {
      // Permitimos solo 2 decimales para Dólares
      value = value.replace(/[^0-9.]/g, ''); // Permitimos solo números y punto
      const decimalCount = (value.match(/\./g) || []).length;
      if (decimalCount > 1) {
        // Si hay más de un punto decimal, eliminamos el último
        value = value.substring(0, value.lastIndexOf('.')) + value.substring(value.lastIndexOf('.') + 1);
      }
      const parts = value.split('.');
      if (parts.length > 1 && parts[1].length > 2) {
        // Limitar a dos decimales
        value = `${parts[0]}.${parts[1].substring(0, 2)}`;
      }
    } else {
      // Para Colones, solo permitimos números enteros
      value = value.replace(/\D/g, '');
    }

    setPropertyPrice(value);
  };

  const handleBlur = () => {
    const numericPrice = parseFloat(propertyPrice);
    const minPrice = propertyCurrency === 'USD' ? MIN_PRICE_USD : MIN_PRICE_CRC;
    const maxPrice = propertyCurrency === 'USD' ? MAX_PRICE_USD : MAX_PRICE_CRC;

    if (numericPrice < minPrice) {
      setPropertyPrice(minPrice.toString());
    } else if (numericPrice > maxPrice) {
      setPropertyPrice(maxPrice.toString());
    }
    setIsEditing(false);
  };

  const toggleEditing = () => {
    setIsEditing(!isEditing);
  };

  const handleCurrencyChange = (event: SelectChangeEvent<string>) => {
    setPropertyCurrency(event.target.value);
    setPropertyPrice(''); 
  };

  const formatPrice = (price: string) => {
    const numericPrice = parseFloat(price) || 0;
    return propertyCurrency === 'USD'
      ? `$${numericPrice.toLocaleString('en-US')}`
      : `₡${numericPrice.toLocaleString('es-CR')}`;
  };

  const minPrice = propertyCurrency === 'USD' ? MIN_PRICE_USD : MIN_PRICE_CRC;
  const maxPrice = propertyCurrency === 'USD' ? MAX_PRICE_USD : MAX_PRICE_CRC;

  const isPriceValid =
    parseFloat(propertyPrice) >= minPrice && parseFloat(propertyPrice) <= maxPrice;

  return (
    <Container
      maxWidth="sm"
      sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
    >
      <Box mt={4} p={4} sx={{ width: '100%' }}>
        <Typography variant="h4" gutterBottom textAlign="center">
          Ahora, establece el precio
        </Typography>
        <Typography variant="body1" mb={4} textAlign="center">
          Puedes cambiarlo cuando quieras.
        </Typography>

        {/* Label y selector de moneda estilizado */}
        <Box mb={2}>
          <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
            Moneda
          </Typography>
          <FormControl fullWidth>
            <Select
              value={propertyCurrency}
              onChange={handleCurrencyChange}
              sx={{
                backgroundColor: '#f5f5f5',
                borderRadius: '4px',
                borderColor: 'green',
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'green',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'green',
                },
                '& .MuiSelect-select': {
                  padding: '10px',
                  fontSize: '1rem',
                },
              }}
            >
              <MenuItem
                value="CRC"
                sx={{
                  '&:hover': {
                    backgroundColor: 'rgba(0, 128, 0, 0.1)', 
                  },
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(0, 128, 0, 0.2)',
                  },
                  '&.Mui-selected:hover': {
                    backgroundColor: 'rgba(0, 128, 0, 0.25)',
                  },
                }}
              >
                Colones (₡)
              </MenuItem>
              <MenuItem
                value="USD"
                sx={{
                  '&:hover': {
                    backgroundColor: 'rgba(0, 128, 0, 0.1)',
                  },
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(0, 128, 0, 0.2)',
                  },
                  '&.Mui-selected:hover': {
                    backgroundColor: 'rgba(0, 128, 0, 0.25)', 
                  },
                }}
              >
                Dólares ($)
              </MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Campo para editar el precio */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 4,
        }}
      >
        {isEditing ? (
          <input
            type="text"
            value={propertyPrice}
            onChange={handlePriceChange}
            onBlur={handleBlur}
            inputMode="numeric"
            autoFocus
            style={{
              fontSize: 'clamp(2rem, 8vw, 5rem)', 
              fontWeight: 'bold',
              textAlign: 'center',
              border: 'none',
              outline: 'none',
              width: '100%',
              maxWidth: '90vw',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          />
        ) : (
          <Typography
            variant="h3"
            sx={{
              fontWeight: 'bold',
              fontSize: { xs: '2rem', sm: '2.5rem', md: '4rem' },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            onClick={toggleEditing}
          >
            {formatPrice(propertyPrice)}
            <IconButton size="small" sx={{ ml: 1 }}>
              <EditIcon />
            </IconButton>
          </Typography>
        )}
      </Box>


      </Box>

      <PropertyFooterLayout
        progress={progress}
        step={step}
        onBack={handleBack}
        onContinue={handleContinue}
        isContinueDisabled={!isPriceValid}
      />
    </Container>
  );
};

export default PropertyPricePage;
